import { useCallback, useEffect, useState } from "react";
import { Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * EST-QUAL-OT — the one checkbox an estimator gets, and the numbers beside it.
 *
 * Everything shown here is computed server-side from the estimate's field labour hours (the same
 * total the Field Hours tab publishes) and is read-only on purpose: the ratio is a constant, the
 * amount follows the premium rate in Settings, and both refresh whenever the estimate moves.
 * Ticking it prints the qualification on the proposal as an OPTIONAL ADD, never in the base bid.
 */
export default function AccelerationRow({ estimateId, readOnly = false }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => estApi.getAcceleration(estimateId).then(setD).catch(() => {}),
                           [estimateId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (included) => {
    setBusy(true);
    try {
      setD(await estApi.saveAcceleration(estimateId, included));
      toast.success(included ? "Schedule acceleration will print as an optional add"
                             : "Schedule acceleration removed from the proposal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
    } finally { setBusy(false); }
  };

  if (!d || !d.enabled) return null;
  return (
    <div className="border-b border-slate-200 bg-amber-50/40 px-4 py-2.5"
         data-testid="qual-acceleration">
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={!!d.included} disabled={readOnly || busy}
               onChange={(e) => toggle(e.target.checked)}
               data-testid="qual-acceleration-include" />
        <span className="flex-1">
          <span className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-800">
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            Include Schedule Acceleration
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              optional add — never in the base bid
            </span>
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600"
                data-testid="qual-acceleration-math">
            <b className="text-sm text-slate-900">{money(d.amount)}</b>
            <span><b>{d.ot_hours}</b> OT hours</span>
            <span className="flex items-center gap-1 text-slate-400">
              <Lock className="h-3 w-3" /> {d.ratio_label}
            </span>
            <span>{d.estimated_field_labor_hours} field labour hours ×{" "}
              {money(d.ot_premium_rate)}/h premium</span>
          </span>
          <span className="mt-1 block text-[11px] text-slate-500"
                data-testid="qual-acceleration-info">
            Crew of {d.crew_size} · {money(d.weekly_ot_spend_per_crew)} overtime per crew per week ·{" "}
            {d.weeks_accelerated} weeks instead of {d.weeks_regular} ({d.weeks_saved} saved)
          </span>
          {d.included && (
            <span className="mt-1.5 block rounded border border-slate-200 bg-white px-2 py-1 text-[11px] italic leading-relaxed text-slate-600"
                  data-testid="qual-acceleration-text">
              {d.text}
            </span>
          )}
        </span>
      </label>
    </div>
  );
}
